import { IconRefresh, IconTrash } from "@tabler/icons-react";
import {
	Button,
	Collapse,
	Descriptions,
	Space,
	Switch,
	Typography,
} from "antd";
import { useEffect, useState } from "react";
import { APP_CATALOG, APP_KINDS } from "@/common/app-catalog";
import {
	useOrganizationApps,
	useRotateOrganizationAppCredential,
	useUpdateOrganizationApp,
} from "@/common/artemis-operations/apps";
import { usePlatformFeatures } from "@/common/artemis-operations/platform-features";
import {
	useDeleteGitProviderConnection,
	useGitProviderConnection,
} from "@/common/artemis-operations/workspaces";
import { confirmDestructive } from "@/common/confirm";
import {
	type AgentCredentialReveal,
	type AppKind,
	AppStatus,
	type GitProviderConnection,
	type OrganizationApp,
} from "@/common/model";
import { useNotify } from "@/common/use-notify";
import {
	GitHubInstallationPicker,
	type GitHubReturn,
} from "@/components/admin/github-installation-picker";
import { AgentCredentialModal } from "@/components/agents/agent-credential-modal";
import {
	BaseContainer,
	COPYABLE,
	CustomIcon,
	QueryGuard,
} from "@/components/base";

/**
 * The organization's app catalog (ADR-061): one row per app with the org-level enable switch. GitHub's
 * row holds the organization's one installation (ADR-062) with Connect/Disconnect; Port's holds its
 * machine identity (client id, credential expiry, last authentication, rotation).
 * Org admins only; the route is gated by `useIsOrgAdmin`.
 */
export function OrganizationApps({
	githubReturn,
}: {
	/** A GitHub return that landed on this window with no connect dialog to hand it to. */
	githubReturn?: GitHubReturn;
}) {
	const notify = useNotify();
	const { data: apps, isPending, isError } = useOrganizationApps();
	const { data: features } = usePlatformFeatures();
	const update = useUpdateOrganizationApp();
	const rotate = useRotateOrganizationAppCredential();
	const [reveal, setReveal] = useState<AgentCredentialReveal | null>(null);
	const { data: connection, isPending: connectionPending } =
		useGitProviderConnection();
	const deleteConnection = useDeleteGitProviderConnection();
	const [connectOpen, setConnectOpen] = useState(false);

	useEffect(() => {
		if (githubReturn) setConnectOpen(true);
	}, [githubReturn]);

	function handleDisconnect() {
		if (!connection) return;
		confirmDestructive({
			title: "Disconnect GitHub",
			content:
				"Repositories linked to projects through this connection will no longer be reachable, and the app is uninstalled from the GitHub account.",
			okText: "Disconnect",
			onOk: () =>
				deleteConnection.mutate(connection.id, {
					onSuccess: () => notify.success("GitHub disconnected."),
					onError: (err) =>
						notify.error(
							(err as Error).message || "Failed to disconnect GitHub.",
						),
				}),
		});
	}

	const byKind = new Map((apps ?? []).map((a) => [a.kind, a]));
	// GitHub needs the installation-level App registration (docs/feature-flags.md); Port needs nothing.
	const kinds = APP_KINDS.filter((k) => k !== "GIT_HUB" || features?.gitHub);

	function setStatus(kind: AppKind, status: AppStatus) {
		update.mutate(
			{ kind, status },
			{
				onSuccess: (payload) => {
					notify.success(
						`${APP_CATALOG[kind].name} ${status === AppStatus.ENABLED ? "enabled" : "disabled"} for the organization.`,
					);
					if (payload.credentials) setReveal(payload.credentials);
				},
				onError: (err) =>
					notify.error((err as Error).message || "Failed to update the app."),
			},
		);
	}

	function handleToggle(kind: AppKind, checked: boolean) {
		if (checked) {
			setStatus(kind, AppStatus.ENABLED);
			return;
		}
		confirmDestructive({
			title: `Disable ${APP_CATALOG[kind].name} for the organization`,
			content:
				"Every workspace loses access immediately. Workspace settings are kept, so re-enabling restores them.",
			okText: "Disable",
			onOk: () => setStatus(kind, AppStatus.DISABLED),
		});
	}

	function handleRotate(kind: AppKind) {
		confirmDestructive({
			title: "Rotate credential",
			content:
				"The current secret stops working immediately. The new one is shown once.",
			okText: "Rotate",
			onOk: () =>
				rotate.mutate(
					{ kind },
					{
						onSuccess: (payload) => setReveal(payload.credentials),
						onError: (err) =>
							notify.error(
								(err as Error).message || "Failed to rotate the credential.",
							),
					},
				),
		});
	}

	return (
		<BaseContainer>
			<QueryGuard isPending={isPending} isError={isError} isNull={false}>
				<Collapse
					style={{ width: "100%" }}
					items={kinds.map((kind) => {
						const app = byKind.get(kind);
						const enabled = app?.status === AppStatus.ENABLED;
						return {
							key: kind,
							label: APP_CATALOG[kind].name,
							extra: (
								// antd requires an explicit stopPropagation here, else clicking
								// `extra` also toggles the panel.
								<Space onClick={(e) => e.stopPropagation()}>
									<Switch
										size="small"
										checked={enabled}
										loading={update.isPending}
										onChange={(checked) => handleToggle(kind, checked)}
										aria-label={`Enable ${APP_CATALOG[kind].name}`}
									/>
								</Space>
							),
							children: (
								<AppPanel
									kind={kind}
									app={app}
									onRotate={() => handleRotate(kind)}
									rotating={rotate.isPending}
									connection={
										connectionPending ? undefined : (connection ?? null)
									}
									onConnect={() => setConnectOpen(true)}
									onDisconnect={handleDisconnect}
									disconnecting={deleteConnection.isPending}
								/>
							),
						};
					})}
				/>
			</QueryGuard>
			<AgentCredentialModal
				credentials={reveal}
				onClose={() => setReveal(null)}
				title="Port credential"
				showEnvBlock={false}
			/>
			<GitHubInstallationPicker
				open={connectOpen}
				githubReturn={githubReturn}
				onClose={() => setConnectOpen(false)}
			/>
		</BaseContainer>
	);
}

function AppPanel({
	kind,
	app,
	onRotate,
	rotating,
	connection,
	onConnect,
	onDisconnect,
	disconnecting,
}: {
	kind: AppKind;
	app?: OrganizationApp;
	onRotate: () => void;
	rotating: boolean;
	/** The organization's GitHub connection: null when none, undefined while loading. */
	connection: GitProviderConnection | null | undefined;
	onConnect: () => void;
	onDisconnect: () => void;
	disconnecting: boolean;
}) {
	const workspaces = app?.workspaces ?? [];
	const items = [
		{
			key: "enabled-in",
			label: "Enabled in",
			children: workspaces.length
				? workspaces.map((w) => w.name).join(", ")
				: "No workspace yet",
		},
	];
	if (kind === "GIT_HUB" && connection) {
		items.unshift(
			{ key: "account", label: "Account", children: connection.accountLogin },
			{
				key: "installation",
				label: "Installation ID",
				children: connection.installationId,
			},
		);
	}
	if (kind === "PORT" && app?.clientId) {
		items.push(
			{
				key: "client-id",
				label: "Client ID",
				children: (
					<Typography.Text code copyable={{ ...COPYABLE, text: app.clientId }}>
						{app.clientId}
					</Typography.Text>
				) as unknown as string,
			},
			{
				key: "expires",
				label: "Secret expires",
				children: app.clientSecretExpiresAt
					? new Date(app.clientSecretExpiresAt as string).toLocaleDateString()
					: "Never",
			},
			{
				key: "last-seen",
				label: "Last authenticated",
				children: app.lastSeenAt
					? new Date(app.lastSeenAt as string).toLocaleString()
					: "Never",
			},
		);
	}

	return (
		<Space orientation="vertical" size="middle" style={{ width: "100%" }}>
			<Typography.Text>{APP_CATALOG[kind].description}</Typography.Text>
			<Descriptions column={1} items={items} />
			{kind === "PORT" && app?.clientId && (
				<Button
					icon={<CustomIcon icon={IconRefresh} />}
					loading={rotating}
					onClick={onRotate}
				>
					Rotate credential
				</Button>
			)}
			{kind === "GIT_HUB" &&
				app?.status === AppStatus.ENABLED &&
				connection !== undefined &&
				(connection ? (
					<Button
						danger
						type="primary"
						icon={<CustomIcon icon={IconTrash} />}
						loading={disconnecting}
						onClick={onDisconnect}
					>
						Disconnect
					</Button>
				) : (
					<Button type="primary" onClick={onConnect}>
						Connect
					</Button>
				))}
		</Space>
	);
}
